/*
  Reinicio de los stores con datos de empresa.

  El problema: cambiar de empresa (canje de portal: `elegirPortal` del
  auth-store, que dispara `<PortalGuard>` sin recargar la página) no avisaba a
  nadie, y los stores Zustand (alertas, precios dinámicos, clientes...)
  seguían mostrando lo de la empresa anterior hasta que alguna pantalla
  volviera a pedir los datos. Los persistidos (`persist`) incluso sobrevivían
  a una recarga. El cambio de organización (`switchOrganization`) recarga la
  página desde `org-switcher.tsx`, así que la memoria muere sola, pero lo
  persistido no; también se cubre acá, por si algún día deja de recargar.

  Cómo se evitan los imports circulares: el auth-store NO importa este módulo.
  Este módulo importa el auth-store y todos los stores de la lista, y se
  arranca UNA vez desde `main.tsx` (`iniciarReinicioPorEmpresa()`). Un store
  nuevo se agrega a `STORES_POR_EMPRESA` (o a `STORES_EXENTOS`, con su
  porqué): el test `reinicio-por-empresa.test.ts` falla si falta.
*/
import type { StoreApi } from 'zustand';
import { useAuthStore } from '@/features/auth/store/auth-store';
import { useChatStore } from '@/features/ai-assistant/store/chat-store';
import { useAiIntelligenceStore } from '@/features/ai-intelligence/store/ai-intelligence-store';
import { useAnalyticsStore } from '@/features/analytics/store/analytics-store';
import { useApprovalWorkflowStore } from '@/features/approval-workflow/store/approval-workflow-store';
import { useApprovalStore } from '@/features/approvals/store/approval-store';
import { useImportacion } from '@/features/comercia/store/importacion-store';
import { useCustomerStore } from '@/features/customers/store/customer-store';
import { useAlertasStore } from '@/features/dynamic-pricing/store/alertas-store';
import { useConfiguracionDelModulo } from '@/features/dynamic-pricing/store/configuracion-del-modulo-store';
import { useDynamicPricingStore } from '@/features/dynamic-pricing/store/dynamic-pricing-store';
import { useImportTrackingStore } from '@/features/import-tracking/store/import-tracking-store';
import { useIntegrationStore } from '@/features/integrations/store/integration-store';
import { useMasterDataStore } from '@/features/master-data/store/master-data-store';
import { useMaterialStore } from '@/features/materials/store/material-store';
import { useEmpresaStore } from '@/features/mi-empresa/store/empresa-store';
import { useNotificationStore } from '@/features/notifications/store/notification-store';
import { useOpportunitiesStore } from '@/features/opportunities/store/opportunities.store';
import { usePermissionStore } from '@/features/permissions/store/permission-store';
import { useModelStore } from '@/features/pricing-models/store/model-store';
import { useQuoteStore } from '@/features/quotes/store/quote-store';
import { useSalesTrainingStore } from '@/features/sales-training/store/sales-training-store';
import { useUserStore } from '@/features/users/store/user-store';

/** Lo mínimo que se necesita de un store de Zustand (con o sin `persist`). */
interface StoreReiniciable {
  setState: StoreApi<unknown>['setState'];
  getInitialState: StoreApi<unknown>['getInitialState'];
  persist?: { clearStorage: () => void };
}

export interface StoreDeEmpresa {
  /** Ruta bajo `src/`; el test la cruza con los archivos de stores que existen. */
  archivo: string;
  store: StoreReiniciable;
}

export const STORES_POR_EMPRESA: StoreDeEmpresa[] = [
  { archivo: 'features/ai-assistant/store/chat-store.ts', store: useChatStore as StoreReiniciable },
  {
    archivo: 'features/ai-intelligence/store/ai-intelligence-store.ts',
    store: useAiIntelligenceStore as StoreReiniciable,
  },
  {
    archivo: 'features/analytics/store/analytics-store.ts',
    store: useAnalyticsStore as StoreReiniciable,
  },
  {
    archivo: 'features/approval-workflow/store/approval-workflow-store.ts',
    store: useApprovalWorkflowStore as StoreReiniciable,
  },
  {
    archivo: 'features/approvals/store/approval-store.ts',
    store: useApprovalStore as StoreReiniciable,
  },
  {
    archivo: 'features/comercia/store/importacion-store.ts',
    store: useImportacion as StoreReiniciable,
  },
  {
    archivo: 'features/customers/store/customer-store.ts',
    store: useCustomerStore as StoreReiniciable,
  },
  {
    archivo: 'features/dynamic-pricing/store/alertas-store.ts',
    store: useAlertasStore as StoreReiniciable,
  },
  {
    archivo: 'features/dynamic-pricing/store/configuracion-del-modulo-store.ts',
    store: useConfiguracionDelModulo as StoreReiniciable,
  },
  {
    archivo: 'features/dynamic-pricing/store/dynamic-pricing-store.ts',
    store: useDynamicPricingStore as StoreReiniciable,
  },
  {
    archivo: 'features/import-tracking/store/import-tracking-store.ts',
    store: useImportTrackingStore as StoreReiniciable,
  },
  {
    archivo: 'features/integrations/store/integration-store.ts',
    store: useIntegrationStore as StoreReiniciable,
  },
  {
    archivo: 'features/master-data/store/master-data-store.ts',
    store: useMasterDataStore as StoreReiniciable,
  },
  {
    archivo: 'features/materials/store/material-store.ts',
    store: useMaterialStore as StoreReiniciable,
  },
  {
    archivo: 'features/mi-empresa/store/empresa-store.ts',
    store: useEmpresaStore as StoreReiniciable,
  },
  {
    archivo: 'features/notifications/store/notification-store.ts',
    store: useNotificationStore as StoreReiniciable,
  },
  // Persistido: sus filtros (tiendas, organización...) son de la empresa.
  {
    archivo: 'features/opportunities/store/opportunities.store.ts',
    store: useOpportunitiesStore as StoreReiniciable,
  },
  {
    archivo: 'features/permissions/store/permission-store.ts',
    store: usePermissionStore as StoreReiniciable,
  },
  {
    archivo: 'features/pricing-models/store/model-store.ts',
    store: useModelStore as StoreReiniciable,
  },
  { archivo: 'features/quotes/store/quote-store.ts', store: useQuoteStore as StoreReiniciable },
  {
    archivo: 'features/sales-training/store/sales-training-store.ts',
    store: useSalesTrainingStore as StoreReiniciable,
  },
  { archivo: 'features/users/store/user-store.ts', store: useUserStore as StoreReiniciable },
];

/**
 * Stores sin datos de empresa. Cada uno dice por qué: un store nuevo tiene que
 * caer en una de las dos listas.
 */
export const STORES_EXENTOS: { archivo: string; porque: string }[] = [
  {
    archivo: 'features/auth/store/auth-store.ts',
    porque:
      'Es el que dispara el reinicio; su cambio de empresa lo maneja `abrirSesion` y el logout.',
  },
  {
    archivo: 'features/admin/store/admin-store.ts',
    porque: 'Panel de plataforma: planes y suscripciones globales, no de una empresa.',
  },
  {
    archivo: 'features/ai-productivity/store/productivity-store.ts',
    porque: 'Cronómetro personal de la persona, igual en todas sus empresas.',
  },
  {
    archivo: 'features/guided-tour/store/tour-store.ts',
    porque: 'Avance del recorrido guiado: preferencia de la persona.',
  },
  {
    archivo: 'features/onboarding/store/onboarding-store.ts',
    porque: 'Estado de la bienvenida de la persona; el logout lo conserva a propósito.',
  },
  {
    archivo: 'features/landing/store/book-demo-store.ts',
    porque: 'Formulario público de la landing, sin sesión ni empresa.',
  },
  {
    archivo: 'stores/sidebar-store.ts',
    porque: 'Estado visual de la barra lateral (abierta o cerrada).',
  },
  { archivo: 'stores/theme-store.ts', porque: 'Tema claro u oscuro: preferencia de la persona.' },
  {
    archivo: 'features/mini-prixia/plan-completo-store.ts',
    porque: 'Sólo dice si el cuadro «parte del plan completo» está abierto: no guarda datos.',
  },
  {
    archivo: 'features/mini-prixia/progreso-de-misiones.ts',
    porque:
      'El avance de las misiones ya va POR EMPRESA (clave = empresa del token, I6): cambiar de empresa no mezcla nada.',
  },
];

/** Devuelve un store a su estado inicial y, si persiste, borra lo guardado. */
function reiniciarStore({ store }: StoreDeEmpresa): void {
  store.setState(store.getInitialState(), true);
  // Después del setState: éste reescribe lo persistido con el estado inicial.
  store.persist?.clearStorage();
}

export function reiniciarStoresPorEmpresa(): void {
  for (const entrada of STORES_POR_EMPRESA) reiniciarStore(entrada);
}

let detenerVigente: (() => void) | null = null;

/**
 * Suscribe el reinicio al auth-store. Reinicia cuando:
 *  - cambia la empresa: de una empresa conocida a OTRA (tenant del usuario o
 *    portal activo). `null → empresa` NO cuenta: es la primera llegada tras
 *    recargar (`portalActivo` no se persiste), con los mismos datos de antes;
 *  - cambia la organización activa;
 *  - cambia la persona (modo soporte: entrar o salir);
 *  - se cierra la sesión.
 * Idempotente: arrancarla dos veces deja una sola suscripción (HMR, tests).
 * Devuelve la función para detenerla.
 */
export function iniciarReinicioPorEmpresa(): () => void {
  detenerVigente?.();
  const desuscribir = useAuthStore.subscribe((estado, previo) => {
    const tenantPrevio = previo.user?.tenantId ?? null;
    const tenantNuevo = estado.user?.tenantId ?? null;
    const cambioTenant =
      tenantPrevio !== null && tenantNuevo !== null && tenantPrevio !== tenantNuevo;
    const cambioPortal =
      previo.portalActivo !== null &&
      estado.portalActivo !== null &&
      previo.portalActivo !== estado.portalActivo;
    const cambioOrganizacion = previo.activeOrganizationId !== estado.activeOrganizationId;
    const cerroSesion = previo.isAuthenticated && !estado.isAuthenticated;
    // Revisión M5: otra PERSONA en la misma empresa (entrar o salir de modo soporte) también:
    // lo cargado como el admin no puede verse como la persona, ni al revés.
    const userPrevio = previo.user?.id ?? null;
    const userNuevo = estado.user?.id ?? null;
    const cambioPersona = userPrevio !== null && userNuevo !== null && userPrevio !== userNuevo;
    if (cambioTenant || cambioPortal || cambioOrganizacion || cerroSesion || cambioPersona) {
      reiniciarStoresPorEmpresa();
    }
  });
  const detener = () => {
    desuscribir();
    if (detenerVigente === detener) detenerVigente = null;
  };
  detenerVigente = detener;
  return detener;
}
